import {makeAutoObservable, runInAction} from 'mobx';

import apiService from '../transport/apiService';
import {logger, serializeError} from '../utils/logger';

import type {RootStore} from './rootStore';

interface SortOption {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface FilterFacet {
  type?: string;
  id?: string;
  count?: number;
  name: string;
  query_value: string;
}

interface FilterGroup {
  type: string;
  id: string;
  sell_price_cents: boolean;
  department: boolean;
  specials: boolean;
  name: string;
  facets: FilterFacet[];
  relationshipNames?: string[];
}

interface ParsedFilter {
  name: string;
  query?: string;
  type: string;
  order: number;
  subs?: ParsedFilterSub[];
}

interface ParsedFilterSub {
  name: string;
  query: string;
  type: string;
}

interface FilterDefinition {
  type: string;
  order: number;
}

interface PriceFilter {
  query: string;
  [key: string]: unknown;
}

type SubFiltersMap = Record<string, Set<ParsedFilterSub>>;

export const MIN_PRICE = 0;
export const MAX_PRICE = 1000;

const fixedFilters: FilterGroup[] = [
  // Specials
  {
    type: 'facet-groups',
    id: '62ccff7c25a0f0320612fe4f',
    sell_price_cents: false,
    department: false,
    specials: true,
    name: 'Specials',
    facets: [
      {
        type: 'facets',
        id: '62ccff7c25a0f0320612fe51',
        count: 8,
        name: 'Specials',
        query_value: 'special:drill',
      },
    ],
    relationshipNames: ['facets'],
  },
  // Price range
  {
    type: 'facet-groups',
    id: '62ccff7c25a0f0320612fe50',
    sell_price_cents: true,
    department: false,
    specials: false,
    name: 'Price',
    facets: [
      {
        name: 'Price',
        query_value: 'price_range:1000-2000',
      },
    ],
  },
];

export class FilterStore {
  rootStore: RootStore;

  isLoadingFilterOptions = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    this.reset();
  }

  reset = () => {
    this.selectedSort = null;
  };

  //
  // SORT
  //
  selectedSort: SortOption | null = null;
  sortOptions: SortOption[] = [];
  topProducts: SortOption | null = null;

  // TODO(DIS-852): rangeStore.setSelectedSortOption() is now a no-op for
  // RangeProductListScreen (reads from SearchContext). Keep for other consumers.
  setSelectedSort = (item: SortOption) => {
    runInAction(() => {
      this.selectedSort = item;
      this.rootStore.rangeStore.setSelectedSortOption(item);
    });
  };

  loadSortOptions = async () => {
    runInAction(() => {
      this.sortOptions = [];
      this.isLoadingFilterOptions = true;
    });
    try {
      let data = (await apiService.getSortOptions()) as SortOption[];
      data = data.filter(
        (item: SortOption) => !['unit_price', 'discount_percent'].includes(item.id),
      );
      runInAction(() => {
        this.topProducts = data.shift() || null;
        this.sortOptions = data;
      });
    } catch (err) {
      logger.error('FilterStore', 'Error loading sort options', serializeError(err));
    } finally {
      runInAction(() => {
        this.isLoadingFilterOptions = false;
      });
    }
  };

  //
  // SORT AND FILTER
  //
  // Need to clear both sort and filters
  // AND
  // hit clear rangeStore selection so it doesn't
  // cause multiple reloads of products.
  clearSortAndFilters = () => {
    runInAction(() => {
      this.selectedSort = this.topProducts;
      this.priceSelected = null;
      this.low = MIN_PRICE;
      this.high = MAX_PRICE;
      this.specialsSelected = null;
      this.subFilters = {};
      this.currentFilters = [];
      this.previousFilters = this.currentFilters;
      this.rootStore.rangeStore.clearSelectedSortAndFilter(this.topProducts);
    });
  };

  //
  // FILTER
  //
  currentFilters: ParsedFilter[] = [];
  previousFilters: ParsedFilter[] = [];
  filterOptions: ParsedFilter[] | null = null;
  specialsSelected: ParsedFilter | null = null;
  priceSelected: PriceFilter | null = null;
  low = 0;
  high = 1000;
  currentParentFilter: ParsedFilter | null = null;
  subFilters: SubFiltersMap = {};

  loadFilterOptions = async () => {
    try {
      const data = (await apiService.getFilterOptions()) as FilterGroup[];
      const parsed = parseFilterResults(data);
      runInAction(() => {
        this.filterOptions = parsed;
      });
    } catch (err) {
      logger.error('FilterStore', 'Error loading filter options', serializeError(err));
    }
  };

  setFilters = (filters: ParsedFilter[]) => (this.currentFilters = filters);
  setCurrentParentFilter = (filter: ParsedFilter) => (this.currentParentFilter = filter);

  setSpecialsFilter = (filter: ParsedFilter) => {
    if (this.specialsSelected) {
      this.specialsSelected = null;
    } else {
      this.specialsSelected = filter;
    }
  };

  setPriceFilter = (low: number, high: number, item: ParsedFilter) => {
    if (low > MIN_PRICE || high < MAX_PRICE) {
      this.priceSelected = {
        ...item,
        query: `price_range:${low * 100}-${high * 100}`,
      };
    } else {
      this.priceSelected = null;
    }
    this.low = low;
    this.high = high;
  };

  // { brands: [{filter1}, filter2}...],
  //   countries: [{filter1}, {filter2}...]...}
  selectSubFilter = (item: ParsedFilterSub) => {
    // Use the spread operator to create a new object. React won't re-render
    // if we just modify the old object. Support multiple subfilters per category
    if (!this.currentParentFilter) return;

    const parentName = this.currentParentFilter.name;
    const newFilters = {...this.subFilters};

    if (newFilters[parentName]?.has(item)) {
      // item already exists in the filter list, remove it.
      newFilters[parentName].delete(item);
    } else if (newFilters[parentName]) {
      // parent filter exists, just add the subfilter to it.
      newFilters[parentName].add(item);
    } else {
      // parent filter doesn't exist, give it a new set
      newFilters[parentName] = new Set();
      newFilters[parentName].add(item);
    }

    runInAction(() => {
      this.subFilters = newFilters;
    });
  };

  filtersChanging = () => {
    this.previousFilters = this.currentFilters;
  };

  clearSubFilters = () => {
    if (!this.currentParentFilter) return;

    const parentName = this.currentParentFilter.name;
    const newFilters = {...this.subFilters};
    newFilters[parentName]?.clear();
    this.subFilters = newFilters;
  };

  // TODO(DIS-852): rangeStore.clearSelectedFilterOptions() is now a no-op for
  // RangeProductListScreen (reads from SearchContext). Keep for other consumers.
  clearFilters = () => {
    runInAction(() => {
      this.priceSelected = null;
      this.low = MIN_PRICE;
      this.high = MAX_PRICE;
      this.specialsSelected = null;
      this.subFilters = {};
      this.currentFilters = [];
      this.previousFilters = this.currentFilters;
    });
    this.rootStore.rangeStore.clearSelectedFilterOptions();
  };
}

const parseFilterResults = (data: FilterGroup[]): ParsedFilter[] => {
  data.unshift(...fixedFilters);
  // remove departments
  const definitions: Record<string, FilterDefinition> = {
    Specials: {
      type: 'SINGLE',
      order: 1,
    },
    Price: {
      type: 'SLIDER',
      order: 2,
    },
    Brands: {
      type: 'SUB-SCREEN',
      order: 3,
    },
    Countries: {
      type: 'SUB-SCREEN',
      order: 4,
    },
    Regions: {
      type: 'SUB-SCREEN',
      order: 5,
    },
  };
  let parsed = data
    .filter((group: FilterGroup) => group.name !== 'Departments')
    .filter((group: FilterGroup) => definitions[group.name] !== undefined)
    .map((group: FilterGroup): ParsedFilter => {
      const definition = definitions[group.name];
      if (definition.type === 'SINGLE' || definition.type === 'SLIDER') {
        return {
          name: group.name,
          query: group.facets[0].query_value,
          type: definition.type,
          order: definition.order,
        };
      } else {
        return {
          name: group.name,
          subs: group.facets.map((facet: FilterFacet): ParsedFilterSub => {
            return {
              name: facet.name,
              query: facet.query_value,
              type: 'SUB-SCREEN-SINGLE',
            };
          }),
          type: definition.type,
          order: definition.order,
        };
      }
    });
  // Order options
  parsed = parsed.sort((a: ParsedFilter, b: ParsedFilter) => a.order - b.order);
  return parsed;
};
